import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { api, type DashboardSummary } from '@/lib/api';
import { Card, CardBody, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge, statusTone } from '@/components/ui/badge';
import { relativeTime } from '@/lib/utils';

export function DashboardPage() {
  const summary = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api.get<DashboardSummary>('/api/dashboard/summary'),
    refetchInterval: 15_000,
  });

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-semibold text-slate-900">Dashboard</h1>
        <p className="text-sm text-slate-500 mt-1">Fleet status at a glance.</p>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          label="Active tenants"
          value={summary.data?.tenants_by_status.active ?? 0}
          hint={`${summary.data?.tenants_by_status.awaiting_auth ?? 0} awaiting auth`}
        />
        <StatCard
          label="Suspended"
          value={summary.data?.tenants_by_status.suspended ?? 0}
        />
        <StatCard
          label="Servers"
          value={summary.data?.servers_by_status.active ?? 0}
          hint={`${summary.data?.servers_by_status.draining ?? 0} draining`}
        />
        <StatCard
          label="Capacity"
          value={
            summary.data
              ? `${summary.data.capacity_used} / ${summary.data.capacity_total}`
              : '—'
          }
          hint={
            summary.data && summary.data.capacity_total > 0
              ? `${Math.round((summary.data.capacity_used / summary.data.capacity_total) * 100)}% utilized`
              : ''
          }
        />
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Recent activity</CardTitle>
        </CardHeader>
        <CardBody>
          {summary.isLoading ? (
            <div className="text-sm text-slate-500">Loading…</div>
          ) : !summary.data?.recent_activity?.length ? (
            <div className="text-sm text-slate-500">No activity yet.</div>
          ) : (
            <ul className="divide-y divide-slate-100">
              {summary.data.recent_activity.map((entry) => (
                <li key={entry.id} className="py-2.5 flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between text-sm">
                  <div className="flex items-center gap-3 min-w-0">
                    <Badge tone={statusTone(entry.outcome)}>{entry.outcome}</Badge>
                    <span className="font-mono text-xs text-slate-600">{entry.action}</span>
                    {entry.target_kind && entry.target_id && (
                      <Link
                        to={
                          entry.target_kind === 'tenant'
                            ? `/tenants/${entry.target_id}`
                            : entry.target_kind === 'server'
                            ? `/servers/${entry.target_id}`
                            : '#'
                        }
                        className="text-xs text-slate-700 hover:text-slate-900 truncate"
                      >
                        {entry.target_kind}:{entry.target_id}
                      </Link>
                    )}
                  </div>
                  <div className="text-xs text-slate-500 whitespace-nowrap">
                    {entry.operator_email ?? 'system'} · {relativeTime(entry.created_at)}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardBody>
      </Card>
    </div>
  );
}

function StatCard({ label, value, hint }: { label: string; value: number | string; hint?: string }) {
  return (
    <Card>
      <CardBody>
        <div className="text-xs uppercase tracking-wide text-slate-500">{label}</div>
        <div className="text-2xl font-semibold text-slate-900 mt-1">{value}</div>
        {hint ? <div className="text-xs text-slate-500 mt-1">{hint}</div> : null}
      </CardBody>
    </Card>
  );
}
